import React from "react";

// Remediation Block Component
function RemediationBlock({ remediation, fieldId, onAddNote, onAddAttachment, readOnly = true }) {
  // Check if there's actual content
  const notes = remediation?.notes || [];
  const attachments = remediation?.attachments || [];
  const hasContent = notes.length > 0 || attachments.length > 0;
  
  // In readOnly mode (PDF/print), only show if there's content
  if (readOnly && !hasContent) {
    return null;
  }

  return (
    <div className="mt-3 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-yellow-700 font-semibold text-sm">⚠️ Remediation</span>
      </div>

      {/* Notes */}
      {notes.map((note, idx) => (
        <div key={note.id || idx} className="mb-2 p-2 bg-white rounded border border-yellow-200">
          <div className="text-sm text-gray-800">{note.text}</div>
          <div className="text-xs text-gray-500 mt-1">
            {note.createdBy} • {note.createdAt && new Date(note.createdAt.seconds * 1000).toLocaleString()}
          </div>
        </div>
      ))}

      {/* Attachments */}
      {attachments.map((file, idx) => (
        <div key={file.url || idx} className="mb-2">
          <div className="text-xs text-gray-600 mb-1">
            📎 {file.name} 
            <span className="text-gray-400 ml-2">
              by {file.uploadedBy} • {file.uploadedAt && new Date(file.uploadedAt.seconds * 1000).toLocaleString()}
            </span>
          </div>
          {file.name && (file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) || file.url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) ? (
            <img src={file.url} alt={file.name} className="max-w-xs rounded border" />
          ) : (
            <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
              View File
            </a>
          )}
        </div>
      ))}

      {/* Add Actions (only if not readOnly) */}
      {!readOnly && (
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => onAddNote?.(fieldId)}
            className="text-xs px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700"
          >
            ➕ Add Note
          </button>
          <button
            onClick={() => onAddAttachment?.(fieldId)}
            className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            📎 Attach File
          </button>
        </div>
      )}
    </div>
  );
}

export default function PremiumReportRenderer({ 
  formDef = [], 
  responses = {}, 
  changeLog = [],
  remediation = {},
  onAddNote,
  onAddAttachment,
  readOnly = true
}) {
  if (!Array.isArray(formDef)) {
    return <div className="text-red-600">Invalid form definition</div>;
  }

  // Build visibility rules from branch maps so conditional logic hides non-triggered fields
  const visibilityRules = buildVisibilityRules(formDef);

  return (
    <div className="space-y-6">
      {formDef.map((field) =>
        renderField(field, responses, changeLog, remediation, onAddNote, onAddAttachment, readOnly, visibilityRules)
      )}
    </div>
  );
}

// Removed AssetDisplay component - asset data is already in the submission snapshot

function renderField(field, responses, changeLog = [], remediation = {}, onAddNote, onAddAttachment, readOnly, visibilityRules) {
  if (!isFieldVisible(field, responses, visibilityRules)) {
    return null;
  }

  const value = responses?.[field.id]?.value;
  
  // Check if this field was edited (including section children)
  const wasEdited = changeLog.some(c => 
    c.fieldId === field.id || 
    c.fieldId?.startsWith(field.id + ".")
  );

  // INSTRUCTION BLOCK
  if (field.type === "instruction") {
    return (
      <div key={field.id} className="bg-gray-100 p-4 rounded text-gray-700 italic">
        {field.config?.text || field.label || field.question}
        {field.attachments && field.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {field.attachments.map((att, i) => (
              <img key={i} src={att.url} className="w-32 h-32 object-cover rounded" alt="Reference" />
            ))}
          </div>
        )}
      </div>
    );
  }

  // SECTION (RECURSIVE)
  if (field.type === "section" || field.type === "split_workflow") {
    const directSectionResponse = responseForField(responses, field);
    const sectionValue = directSectionResponse && typeof directSectionResponse === "object" && "value" in directSectionResponse
      ? directSectionResponse.value
      : directSectionResponse;
    const childFields = getChildFields(field);

    const fallbackSectionValue = isEmptyResponse(sectionValue)
      ? buildSectionResponseFromChildren(responses, field)
      : sectionValue;

    const instances = Array.isArray(fallbackSectionValue)
      ? fallbackSectionValue
      : Array.isArray(fallbackSectionValue?.instances)
        ? fallbackSectionValue.instances
        : fallbackSectionValue && typeof fallbackSectionValue === "object"
          ? [{ values: fallbackSectionValue }]
          : [];

    return (
      <div key={field.id} className="section-block border-2 rounded-lg p-5 bg-gray-50">
        <div className="section-title text-lg font-bold mb-4 pb-2 border-b-2">
          {field.label || field.question}
        </div>

        {instances.length === 0 ? (
          <div className="text-sm text-gray-500 italic">
            No entries submitted
          </div>
        ) : (
          instances.map((instance, idx) => {
            // Normalize instance into response-like shape.
            const instanceValues = normalizeSectionInstance(instance);
            const instanceResponses = {};

            childFields.forEach((childField) => {
              // Try matching by ID first, then by label
              let raw = instanceValues?.[childField.id];
              
              if (raw === undefined && childField.label) {
                // Fallback: try matching by label
                raw = instanceValues?.[childField.label];
              }

              if (raw && typeof raw === 'object' && 'value' in raw) {
                instanceResponses[childField.id] = raw;
              } else if (raw !== undefined) {
                instanceResponses[childField.id] = { value: raw };
              } else {
                instanceResponses[childField.id] = { value: null };
              }
            });

            return (
              <div key={idx} className="instance-box border rounded-lg p-4 bg-white mb-3">
                <div className="flex items-center gap-2 mb-3">
                  <div className="font-semibold text-blue-700">
                    Instance #{idx + 1}
                  </div>
                  {instance._addedAfterSubmission && (
                    <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs rounded font-medium">
                      Added after submission
                    </span>
                  )}
                </div>

                {childFields.map((childField) =>
                  renderField(
                    { ...childField, __isSectionChild: true },
                    instanceResponses,
                    changeLog,
                    remediation,
                    onAddNote,
                    onAddAttachment,
                    readOnly,
                    visibilityRules
                  )
                )}
              </div>
            );
          })
        )}
      </div>
    );
  }

  // NORMAL FIELD
  return (
    <div 
      key={field.id} 
      className={`field-block border rounded-lg p-4 bg-white ${wasEdited ? 'border-l-4 border-yellow-400 bg-yellow-50' : ''}`}
    >
      <div className="question font-bold text-sm mb-2">
        {field.label || field.question}
        {wasEdited && (
          <span className="ml-2 px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs rounded font-medium">
            Edited
          </span>
        )}
      </div>
      
      {field.attachments && field.attachments.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-gray-600 mb-1">Reference Materials:</div>
          <div className="flex flex-wrap gap-2">
            {field.attachments.map((att, i) => (
              <img key={i} src={att.url} className="w-32 h-32 object-cover rounded border" alt="Reference" />
            ))}
          </div>
        </div>
      )}
      
      <div className="answer pl-3 border-l-3 border-gray-200">
        {renderValue(field, value)}
      </div>

      {/* Add Remediation Block */}
      <RemediationBlock
        remediation={remediation?.[field.id]}
        fieldId={field.id}
        onAddNote={onAddNote}
        onAddAttachment={onAddAttachment}
        readOnly={readOnly}
      />
    </div>
  );
}

// Build a lookup of fieldId -> [{ sourceId, test }]
function buildVisibilityRules(formDef) {
  const rules = {};

  const walk = (fields = []) => {
    fields.forEach((f) => {
      const groups = getBranchGroups(f);
      groups.forEach((g) => {
        const cfg = f.branchMap?.[g.key];
        if (!cfg?.targets?.length) return;
        cfg.targets.forEach((tid) => {
          if (!rules[tid]) rules[tid] = [];
          rules[tid].push({ sourceId: f.id, test: g.test });
        });
      });

      // Recurse into nested structures (sections / children)
      if (Array.isArray(f.fields)) walk(f.fields);
      if (Array.isArray(f.children)) walk(f.children);
      if (Array.isArray(f.instances)) {
        f.instances.forEach((inst) => walk(inst.children || []));
      }
    });
  };

  walk(formDef);
  return rules;
}

function isFieldVisible(field, responses, visibilityRules) {
  const rules = visibilityRules?.[field.id];
  if (!rules || rules.length === 0) return true; // No branching configured

  return rules.some(({ sourceId, test }) => {
    const raw = responses?.[sourceId]?.value ?? responses?.[sourceId];
    return test(raw);
  });
}

function normalizeSectionInstance(instance) {
  if (!instance || typeof instance !== "object") return {};
  if (instance.values && typeof instance.values === "object") return instance.values;
  if (instance.responses && typeof instance.responses === "object") return instance.responses;
  if (instance.fields && typeof instance.fields === "object" && !Array.isArray(instance.fields)) return instance.fields;
  return instance;
}

function responseForField(responses = {}, field = {}) {
  const direct = responses?.[field.id];
  if (direct !== undefined) return direct;
  if (field.label && responses?.[field.label] !== undefined) return responses[field.label];
  return undefined;
}

function isEmptyResponse(value) {
  return value === undefined
    || value === null
    || value === ""
    || (Array.isArray(value) && value.length === 0)
    || (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);
}

function getChildFields(field = {}) {
  return Array.isArray(field.children) && field.children.length > 0
    ? field.children
    : Array.isArray(field.fields)
      ? field.fields
      : [];
}

function buildSectionResponseFromChildren(responses = {}, field = {}) {
  const childFields = getChildFields(field);
  const values = {};

  childFields.forEach((childField) => {
    const childResponse = responseForField(responses, childField);
    if (!isEmptyResponse(childResponse)) {
      values[childField.id] = childResponse;
    }
  });

  return Object.keys(values).length ? { instances: [{ values }] } : undefined;
}

function isImageUrl(url = "") {
  return /\.(png|jpe?g|gif|webp|bmp|heic)(\?|$)/i.test(String(url))
    || String(url).startsWith("data:image")
    || String(url).includes("image%2F")
    || String(url).includes("image/");
}

// Minimal branch group definitions (mirrors FormBuilder logic)
function getBranchGroups(field) {
  switch (field.type) {
    case "yesno":
      return [
        { key: "yes", label: "When answer is Yes", test: (v) => String(v?.answer ?? v).toLowerCase() === "yes" },
        { key: "no", label: "When answer is No", test: (v) => String(v?.answer ?? v).toLowerCase() === "no" },
      ];
    case "checkbox":
      return [
        { key: "checked", label: "When checkbox is checked", test: (v) => v === true || v?.checked === true },
        { key: "unchecked", label: "When checkbox is not checked", test: (v) => v === false || v?.checked === false },
      ];
    case "text":
      return [
        { key: "not_blank", label: "When answer is not blank", test: (v) => !!(v?.answer ?? v) && String(v?.answer ?? v).trim().length > 0 },
      ];
    case "number":
      return [
        {
          key: "not_blank",
          label: "When number is provided",
          test: (v) => {
            const actual = v?.number ?? v?.answer ?? v?.value ?? v;
            return actual !== null && actual !== "" && !isNaN(actual);
          },
        },
      ];
    case "date_time":
      return [
        { key: "not_blank", label: "When date/time is provided", test: (v) => !!v },
      ];
    case "signature":
      return [
        { key: "signed", label: "When signed / typed", test: (v) => !!v && (typeof v === "string" ? v.trim().length > 0 : true) },
      ];
    case "multiple_choice":
      return (field.config?.options || []).map((opt) => ({
        key: `opt_${opt.id || opt.label}`,
        label: `When selected: ${opt.label}`,
        test: (v) => (Array.isArray(v) ? v.includes(opt.label) : String(v) === String(opt.label)),
      }));
    case "asset":
    case "location":
      return [
        { key: "not_blank", label: "When a value is selected", test: (v) => !!v && String(v).trim().length > 0 },
      ];
    default:
      return [];
  }
}

function isEmptyValue(val) {
  // Unwrap common nested shapes used by mobile submissions
  if (val && typeof val === "object") {
    if (val.value !== undefined) return isEmptyValue(val.value);
    if (val.answer !== undefined) return isEmptyValue(val.answer);
    if (val.number !== undefined) return isEmptyValue(val.number);

    // Section instances payload
    if (Array.isArray(val.instances)) return val.instances.length === 0;

    // Empty object
    if (Object.keys(val).length === 0) return true;
  }

  if (Array.isArray(val)) return val.length === 0;
  return val == null || val === "";
}

function renderValue(field, value) {
  if (isEmptyValue(value)) {
    return <em className="text-gray-400">Not answered</em>;
  }

  // Special handling for common field names that might be stored as objects
  if ((field.label || field.question || '').includes('comment') && value && typeof value === 'object') {
    if (value.text) return value.text;
    if (value.content) return value.content;
    if (value.message) return value.message;
  }

  switch (field.type) {
    case "text":
      // Handle structured text objects with text property
      if (value && typeof value === 'object' && value.text) {
        return <div className="answer-text">{String(value.text)}</div>;
      }
      return <div className="answer-text">{String(value)}</div>;

    case "number":
      return <strong>{String(value?.number ?? value?.value ?? value)}</strong>;

    case "slider":
      const max = field.config?.max || 10;
      return <strong className="text-lg">{value} / {max}</strong>;

    case "yesno":
      {
        const answer = value?.answer ?? value;
        const normalized = String(answer).toLowerCase();
        return (
          <div>
            <strong>{answer === true || normalized === "yes" ? "Yes" : answer === false || normalized === "no" ? "No" : String(answer)}</strong>
            {value?.followUp && <div className="text-sm text-gray-600 mt-1">Comment: {value.followUp}</div>}
          </div>
        );
      }

    case "checkbox":
      return <strong>{value ? "✓ Checked" : "☐ Unchecked"}</strong>;

    case "date":
    case "date_time":
      try {
        // Handle Firestore timestamp objects
        if (value && typeof value === 'object' && value.seconds) {
          const date = new Date(value.seconds * 1000);
          return field.type === 'date_time' ? date.toLocaleString() : date.toLocaleDateString();
        }
        // Handle structured date objects with date/time properties
        if (value && typeof value === 'object' && (value.date || value.time)) {
          const dateStr = value.date || value.time;
          const timeStr = value.time || value.date;
          if (dateStr && timeStr && field.type === 'date_time') {
            // Combine date and time for date_time fields
            const date = new Date(dateStr);
            const time = new Date(timeStr);
            if (!isNaN(date.getTime()) && !isNaN(time.getTime())) {
              const combined = new Date(date);
              combined.setHours(time.getHours(), time.getMinutes(), time.getSeconds());
              return combined.toLocaleString();
            }
          }
          // Fallback to just the date part
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return field.type === 'date_time' ? date.toLocaleString() : date.toLocaleDateString();
          }
        }
        // Handle ISO strings or other date formats
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          return String(value); // Return original value if not a valid date
        }
        return field.type === 'date_time' ? date.toLocaleString() : date.toLocaleDateString();
      } catch {
        return String(value);
      }

    case "signature":
      // Handle append-only signature instances
      const sigInstances = value?.instances || [value];
      return (
        <div className="space-y-3">
          {sigInstances.filter(Boolean).map((sig, i) => {
            const url = typeof sig === 'string' ? sig : (sig.url || sig.signatureUrl || sig.downloadUrl);
            return (
              <div key={i} className="signature-box border rounded p-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-gray-600">
                    Signature {sigInstances.length > 1 ? `#${i + 1}` : ''}
                  </span>
                  {sig.createdAt && (
                    <span className="text-xs text-gray-500">
                      {new Date(sig.createdAt).toLocaleString()}
                    </span>
                  )}
                  {sig.afterSubmission && (
                    <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs rounded">
                      Added after submission
                    </span>
                  )}
                </div>
                {url && (
                  <img
                    src={url}
                    alt={`Signature ${i + 1}`}
                    className="answer-img max-w-sm border-2 rounded shadow-sm"
                  />
                )}
                {sig.typedName && (
                  <div className="text-sm text-gray-700 mt-2">
                    Signed by: {sig.typedName}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );

    case "media":
    case "photo":
    case "file":
      // Handle append-only media instances
      const mediaInstances = value?.files || value?.instances || [value];
      return (
        <div className="space-y-3">
          {mediaInstances.filter(Boolean).map((file, i) => {
            const url = typeof file === 'string' ? file : (file.url || file.downloadUrl || file.uri);
            const name = file.name || `File ${i + 1}`;
            return (
              <div key={i} className="media-box border rounded p-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-gray-700">{name}</span>
                  {file.uploadedAt && (
                    <span className="text-xs text-gray-500">
                      {new Date(file.uploadedAt).toLocaleString()}
                    </span>
                  )}
                  {file.afterSubmission && (
                    <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs rounded">
                      Added after submission
                    </span>
                  )}
                </div>
                {url && isImageUrl(url) ? (
                  <img
                    src={url}
                    alt={name}
                    className="answer-img max-w-md border rounded shadow-sm"
                  />
                ) : (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{name}</a>
                )}
              </div>
            );
          })}
        </div>
      );

    case "asset":
      // Render asset snapshot data (stored at submission time)
      if (Array.isArray(value)) {
        return <div className="space-y-3">{value.map((asset, index) => <div key={asset?.id || index}>{renderValue(field, asset)}</div>)}</div>;
      }

      if (!value || typeof value !== "object") {
        return (
          <div className="bg-gray-100 p-3 rounded text-sm border border-dashed">
            <span className="text-gray-400 italic">No asset selected</span>
          </div>
        );
      }

      const asset = value;
      
      // DEBUG: Log what data we received
      console.log('🔍 Asset rendering - received data:', {
        hasTitle: !!asset.title,
        hasName: !!asset.name,
        hasFields: !!asset.fields,
        fieldsLength: asset.fields?.length,
        fullAsset: asset
      });
      
      // Resolve asset name from multiple possible locations
      const resolveAssetName = () => {
        if (asset.title) return asset.title;
        if (asset.name) return asset.name;
        // Check dynamic fields array for "name" or "title" field
        const nameField = asset.fields?.find(f => 
          f.label?.toLowerCase() === "name" || 
          f.label?.toLowerCase() === "title"
        );
        if (nameField?.value) return nameField.value;
        return "Unnamed Asset";
      };

      const assetName = resolveAssetName();
      console.log('🔍 Resolved asset name:', assetName);

      // Format timestamp for display
      const formatTimestamp = (timestamp) => {
        if (!timestamp) return null;
        try {
          // Handle Firestore timestamp object
          if (timestamp.seconds) {
            return new Date(timestamp.seconds * 1000).toLocaleString();
          }
          // Handle ISO string
          return new Date(timestamp).toLocaleString();
        } catch {
          return null;
        }
      };

      return (
        <div className="bg-blue-50 p-3 rounded text-sm border border-blue-200">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🏷️</span>
            <strong className="text-gray-900 text-base">
              {assetName}
            </strong>
          </div>
          
          {/* Primary asset properties */}
          {asset.type && (
            <div className="text-xs text-gray-700 mt-1">
              <strong>Type:</strong> {asset.type}
            </div>
          )}
          
          {asset.role && (
            <div className="text-xs text-gray-700 mt-1">
              <strong>Role/Category:</strong> {asset.role}
            </div>
          )}
          
          {/* Legacy top-level properties (if they exist) */}
          {asset.serial && (
            <div className="text-xs text-gray-700 mt-1">
              <strong>Serial:</strong> {asset.serial}
            </div>
          )}
          
          {asset.location && (
            <div className="text-xs text-gray-700 mt-1">
              <strong>Location:</strong> {asset.location}
            </div>
          )}
          
          {asset.category && (
            <div className="text-xs text-gray-700 mt-1">
              <strong>Category:</strong> {asset.category}
            </div>
          )}
          
          {/* Main photo */}
          {asset.mainPhotoURL && (
            <div className="mt-3 mb-3">
              <img 
                src={asset.mainPhotoURL} 
                alt={assetName} 
                className="max-w-xs rounded border shadow-sm"
              />
            </div>
          )}
          
          {/* Dynamic custom fields */}
          {asset.fields && asset.fields.length > 0 && (
            <div className="mt-3 pt-2 border-t border-blue-300">
              <div className="text-xs font-semibold text-gray-700 mb-1">Custom Fields:</div>
              {asset.fields.map((field, idx) => {
                // Format field value based on type
                let displayValue = field.value || "—";
                
                if (field.type === "date" && field.value) {
                  try {
                    displayValue = new Date(field.value).toLocaleDateString();
                  } catch {
                    displayValue = field.value;
                  }
                }
                
                if (field.type === "file" && field.value) {
                  return (
                    <div key={field.id || idx} className="text-xs text-gray-600 pl-2 mb-1">
                      <strong>{field.label}:</strong>{" "}
                      <a 
                        href={field.value} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        📎 View File
                      </a>
                      {field.enableExpiry && (
                        <span className="ml-2 text-orange-600 text-xs">⏰ Tracked</span>
                      )}
                      {field.showOnMobile && (
                        <span className="ml-2 text-green-600 text-xs">📱</span>
                      )}
                    </div>
                  );
                }
                
                return (
                  <div key={field.id || idx} className="text-xs text-gray-600 pl-2 mb-1">
                    <strong>{field.label}:</strong> {displayValue}
                    {field.enableExpiry && field.value && (
                      <span className="ml-2 text-orange-600 text-xs">⏰ Expiry tracked</span>
                    )}
                    {field.showOnMobile && (
                      <span className="ml-2 text-green-600 text-xs" title="Visible on mobile">📱</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Metadata section */}
          <div className="mt-3 pt-2 border-t border-blue-300">
            {asset.id && (
              <div className="text-xs text-gray-500 font-mono mb-1">
                <strong>ID:</strong> {asset.id}
              </div>
            )}
            
            {asset.companyId && (
              <div className="text-xs text-gray-500 mb-1">
                <strong>Company:</strong> {asset.companyId}
              </div>
            )}
            
            {asset.folderId && (
              <div className="text-xs text-gray-500 mb-1">
                <strong>Folder:</strong> {asset.folderId}
              </div>
            )}
            
            {asset.createdAt && (
              <div className="text-xs text-gray-500 mb-1">
                <strong>Created:</strong> {formatTimestamp(asset.createdAt)}
                {asset.createdBy && <span className="ml-1">by {asset.createdBy}</span>}
              </div>
            )}
            
            {asset.updatedAt && (
              <div className="text-xs text-gray-500 mb-1">
                <strong>Updated:</strong> {formatTimestamp(asset.updatedAt)}
                {asset.updatedBy && <span className="ml-1">by {asset.updatedBy}</span>}
              </div>
            )}
          </div>
          
          {/* Admin note (if present) */}
          {asset.adminNote && (
            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
              <strong>Admin Note:</strong> {asset.adminNote}
            </div>
          )}
        </div>
      );

    case "location":
      if (typeof value === "object" && value !== null) {
        if (value.coords?.lat !== undefined && value.coords?.lng !== undefined) {
          return (
            <span className="text-sm">
              Location: {value.coords.lat}, {value.coords.lng}
              {value.coords.accuracy && <div className="text-gray-600">Accuracy: ±{Math.round(value.coords.accuracy)}m</div>}
            </span>
          );
        }
        if (value.latitude && value.longitude) {
          return (
            <span className="text-sm">
              📍 Lat: {value.latitude}, Lon: {value.longitude}
              {value.location && <div className="text-gray-600">{value.location}</div>}
            </span>
          );
        }
        return String(value.location || JSON.stringify(value));
      }
      return String(value);

    case "multiple_choice":
      return <strong className="text-blue-700">{Array.isArray(value) ? value.join(", ") : String(value)}</strong>;

    case "document_number":
      return <code className="bg-gray-100 px-2 py-1 rounded">{String(value)}</code>;

    default:
      // Handle objects that might contain text content
      if (value && typeof value === 'object') {
        // Try to extract text content from common object structures
        if (value.text || value.content || value.message) {
          return String(value.text || value.content || value.message);
        }
        // Handle Firestore document references or complex objects
        if (value.id && value.text) {
          return value.text;
        }
        if (Array.isArray(value.files) || Array.isArray(value.uploads) || Array.isArray(value.attachments)) {
          return renderValue({ ...field, type: "media" }, {
            files: value.files || value.uploads || value.attachments,
          });
        }
        if (value.answer !== undefined) {
          const answer = String(value.answer).toLowerCase();
          return `${answer === "yes" ? "Yes" : answer === "no" ? "No" : String(value.answer)}${value.followUp ? ` - ${value.followUp}` : ""}`;
        }
        if (value.number !== undefined) {
          return String(value.number);
        }
        // For other objects, try to stringify but handle circular references
        try {
          const str = JSON.stringify(value);
          return str.length > 100 ? str.substring(0, 100) + '...' : str;
        } catch {
          return '[Complex Object]';
        }
      }
      return <span className="answer-text">{String(value)}</span>;
  }
}
